import { Box, IconButton, Typography } from "@mui/material"
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useMemo } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { useShallow } from "zustand/shallow"

import DisplayAS400Id from "@/components/common/DisplayAS400Id/DisplayAS400Id"
import { StatusIndicator } from "@/components/common/StatusIndicator/StatusIndicator"
import { FormNumericField } from "@/components/UiKit/FormControls/FormNumericField"
import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import { UOM_RENDER_PRECISION } from "@/constants/formats"
import {
  ComputedProductStatus,
  type CustomerPurchaseOrderPricingTotalsProductConverted,
} from "@/graphql/codegen/graphql"
import { ComputedProductStatusMap } from "@/screens/Companies/products/utils/productStatusMap"
import { useSalesOrderPermissions } from "@/screens/CustomerOrders/hooks/useSalesOrderPermissions"
import type { CustomerPurchaseOrderLineItemsFormValues } from "@/screens/CustomerOrders/schemas"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"
import type { Option } from "@/types/form-fields"
import { createUomsList } from "@/utils/conversion"
import { formatNumber } from "@/utils/number"

interface CustomerPurchaseOrderLineItemHeaderProps {
  lineItemIndex: number
  converted: CustomerPurchaseOrderPricingTotalsProductConverted | null | undefined
  isExpanded: boolean
  onToggle: () => void
}

export const CustomerPurchaseOrderLineItemHeader: FC<CustomerPurchaseOrderLineItemHeaderProps> = ({
  lineItemIndex,
  converted,
  isExpanded,
  onToggle,
}) => {
  const { control } = useFormContext<CustomerPurchaseOrderLineItemsFormValues>()
  const lineItem = useWatch({ control, name: `lineItems.${lineItemIndex}` })
  const { isReadonly } = useSalesOrderPermissions()

  const units = useUnitOfMeasurementsStore(useShallow((state) => state.units))

  // Same createUomsList used by PO's Quantity UoM and the delivery-date line's SO UoM — scoped
  // to this product's own packaging weight UoMs rather than every unit in the system.
  const uomOptions = useMemo<Option<string>[]>(
    () =>
      createUomsList(units, {
        packageUsWeightUom: lineItem.packageUsWeightUom,
        packageMetricWeightUom: lineItem.packageMetricWeightUom,
      }).map((uom) => ({ value: uom.id, label: uom.name ?? uom.id })),
    [units, lineItem.packageUsWeightUom, lineItem.packageMetricWeightUom]
  )

  const convertedUomLabel = units.find((unit) => unit.id === converted?.uomId)?.name ?? converted?.uomId

  return (
    <Box className='flex items-center justify-between gap-4 rounded-t-md bg-primary-200 p-4'>
      <Box className='flex items-center gap-2'>
        <StatusIndicator
          status={lineItem.computedProductStatus ?? ComputedProductStatus.Inactive}
          map={ComputedProductStatusMap}
        />
        <Typography variant='body1' className='text-xs font-bold leading-tight text-gray-700'>
          {lineItem.publicId}
        </Typography>
        <DisplayAS400Id
          id={lineItem.externalSystemId}
          data-testid={`customer-orders.line-item-header.as400-chip-${lineItem.cpoLineItemId}`}
        />
      </Box>

      <Box className='flex items-start gap-8'>
        <Box className='flex flex-col gap-1'>
          <Typography variant='caption' className='text-[10px] font-normal text-gray-700'>
            Customer Name
          </Typography>
          <Typography variant='body2' className='text-xs font-bold text-gray-700'>
            {lineItem.customerDescription ?? "—"}
          </Typography>
        </Box>
        <Box className='flex flex-col gap-1'>
          <Typography variant='caption' className='text-[10px] font-normal text-gray-700'>
            Akrochem Name
          </Typography>
          <Typography variant='body2' className='text-xs font-bold text-gray-700'>
            {lineItem.companyProductDescription ?? "—"}
          </Typography>
        </Box>
        <Box className='flex flex-col gap-1'>
          <Typography variant='caption' className='text-[10px] font-normal text-gray-700'>
            Vendor Name / Description
          </Typography>
          <Typography variant='body2' className='text-xs font-bold text-gray-700'>
            {lineItem.supplierName ?? "—"}
          </Typography>
        </Box>
      </Box>

      <Box className='flex items-center gap-4'>
        <Box className='flex items-center gap-2'>
          <Typography variant='body2' className='whitespace-nowrap text-gray-700'>
            Pallets<span className='text-error-400'>*</span>:
          </Typography>
          <FormNumericField
            control={control}
            name={`lineItems.${lineItemIndex}.pallets`}
            disabled={isReadonly}
            className='w-28'
            data-testid={`customer-orders.line-item-header.pallets-field-${lineItem.cpoLineItemId}`}
          />
        </Box>
        <Box className='flex items-center gap-2'>
          <Typography variant='body2' className='whitespace-nowrap text-gray-700'>
            Units<span className='text-error-400'>*</span>:
          </Typography>
          <FormNumericField
            control={control}
            name={`lineItems.${lineItemIndex}.units`}
            disabled={isReadonly}
            className='w-28'
            data-testid={`customer-orders.line-item-header.units-field-${lineItem.cpoLineItemId}`}
          />
        </Box>
        <Box className='flex items-center gap-2'>
          <Typography variant='body2' className='whitespace-nowrap text-gray-700'>
            UoM<span className='text-error-400'>*</span>:
          </Typography>
          <FormNumericField
            control={control}
            name={`lineItems.${lineItemIndex}.quantity`}
            disabled={isReadonly}
            className='w-28'
            data-testid={`customer-orders.line-item-header.quantity-field-${lineItem.cpoLineItemId}`}
          />
          <FormSelect
            control={control}
            name={`lineItems.${lineItemIndex}.uom`}
            placeholder='Select...'
            options={uomOptions}
            disabled={isReadonly}
            className='w-28'
            data-testid={`customer-orders.line-item-header.uom-select-${lineItem.cpoLineItemId}`}
          />
          <Typography
            variant='body2'
            className='text-gray-800'
            data-testid={`customer-orders.line-item-header.converted-quantity-${lineItem.cpoLineItemId}`}
          >
            {converted ? `${formatNumber(converted.quantity, UOM_RENDER_PRECISION)} ${convertedUomLabel}` : "—"}
          </Typography>
        </Box>

        <IconButton
          onClick={onToggle}
          data-testid={`customer-orders.line-item-header.collapse-button-${lineItem.cpoLineItemId}`}
        >
          {isExpanded ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
        </IconButton>
      </Box>
    </Box>
  )
}
